"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    funds: { name: string; subtitle: string; riskLabel: string }[];
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { equity: "株式", bond: "債券", cash: "現金等" },
    funds: [
      { name: "株式型", subtitle: "例：全世界株式ファンド", riskLabel: "値動き 大" },
      { name: "バランス型", subtitle: "例：株式・債券ミックス", riskLabel: "値動き 中" },
      { name: "債券型", subtitle: "例：国内債券ファンド", riskLabel: "値動き 小" },
    ],
    title: "投資信託は、中身次第で『別の商品』になる",
    description:
      "株式の詰め合わせか、債券の詰め合わせか、ミックスか。同じ『投資信託』という名前でも、リスクとリターンの性格は大きく変わります。",
  },
  en: {
    assets: { equity: "Equities", bond: "Bonds", cash: "Cash & equivalents" },
    funds: [
      { name: "Equity type", subtitle: "e.g. a global equity fund", riskLabel: "Large swings" },
      { name: "Balanced type", subtitle: "e.g. an equity and bond mix", riskLabel: "Medium swings" },
      { name: "Bond type", subtitle: "e.g. a domestic bond fund", riskLabel: "Small swings" },
    ],
    title: "A mutual fund becomes a different product depending on what is inside",
    description:
      "A basket of equities, a basket of bonds, or a mix. Under the same label “mutual fund”, the risk and return character varies a great deal.",
  },
};

// ── Colors by asset class ──────────────────────────────────────────

const ASSET_COLOR = {
  equity: COLOR.sky,
  bond: COLOR.emerald,
  cash: COLOR.amber,
};

// ── Fund definitions ───────────────────────────────────────────────

type Slice = { key: string; color: string; pct: number };

type Fund = {
  slices: Slice[];
  riskTone: "low" | "mid" | "high";
};

const FUNDS: Fund[] = [
  {
    slices: [{ key: "equity", color: ASSET_COLOR.equity, pct: 100 }],
    riskTone: "high",
  },
  {
    slices: [
      { key: "equity", color: ASSET_COLOR.equity, pct: 50 },
      { key: "bond", color: ASSET_COLOR.bond, pct: 45 },
      { key: "cash", color: ASSET_COLOR.cash, pct: 5 },
    ],
    riskTone: "mid",
  },
  {
    slices: [
      { key: "bond", color: ASSET_COLOR.bond, pct: 90 },
      { key: "cash", color: ASSET_COLOR.cash, pct: 10 },
    ],
    riskTone: "low",
  },
];

const RISK_COLOR: Record<Fund["riskTone"], string> = {
  low: COLOR.emerald,
  mid: COLOR.amber,
  high: COLOR.rose,
};

// ── Illustration ───────────────────────────────────────────────────

function FundAssetMixIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const rowH = 56;
  const rowGap = 5;
  const VH = FUNDS.length * rowH + (FUNDS.length - 1) * rowGap;

  const labelX = 18;
  const labelW = 112;
  const barX = labelX + labelW + 10;
  const barW = 220;
  const barH = 34;
  const badgeX = barX + barW + 12;
  const badgeW = 76;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {FUNDS.map((fund, i) => {
        const rowY = i * (rowH + rowGap);
        const rowCy = rowY + rowH / 2;
        const barY = rowCy - barH / 2;

        return (
          <g key={t.funds[i]!.name}>
            {/* Fund name + subtitle (left) */}
            <text x={labelX} y={rowY + 22} fontSize="11" fill="currentColor" fontWeight="700">
              {t.funds[i]!.name}
            </text>
            <text x={labelX} y={rowY + 36} fontSize="7.5" fill="currentColor" fillOpacity={0.5}>
              {t.funds[i]!.subtitle}
            </text>

            {/* Horizontal stacked bar */}
            {(() => {
              let xOffset = 0;
              return fund.slices.map((slice) => {
                const w = (slice.pct / 100) * barW;
                const sx = barX + xOffset;
                xOffset += w;

                const showFull = w >= 48;
                const showPctOnly = !showFull && w >= 22;

                return (
                  <g key={slice.key}>
                    <rect
                      x={sx}
                      y={barY}
                      width={w}
                      height={barH}
                      fill={slice.color}
                      fillOpacity={0.2}
                      stroke={slice.color}
                      strokeWidth="1"
                      strokeOpacity={0.55}
                    />
                    {showFull ? (
                      <text
                        x={sx + w / 2}
                        y={barY + barH / 2 + 3}
                        textAnchor="middle"
                        fontSize="9"
                        fill={slice.color}
                        fontWeight="700"
                      >
                        {t.assets[slice.key]} {slice.pct}%
                      </text>
                    ) : showPctOnly ? (
                      <text
                        x={sx + w / 2}
                        y={barY + barH / 2 + 3}
                        textAnchor="middle"
                        fontSize="8.5"
                        fill={slice.color}
                        fontWeight="700"
                      >
                        {slice.pct}%
                      </text>
                    ) : (
                      <text
                        x={sx + w / 2}
                        y={barY + barH + 10}
                        textAnchor="middle"
                        fontSize="7"
                        fill={slice.color}
                        fontWeight="600"
                      >
                        {slice.pct}%
                      </text>
                    )}
                  </g>
                );
              });
            })()}

            {/* Risk badge (right) */}
            <g>
              <rect
                x={badgeX}
                y={rowCy - 10}
                width={badgeW}
                height={20}
                rx={10}
                fill={RISK_COLOR[fund.riskTone]}
                fillOpacity={0.12}
                stroke={RISK_COLOR[fund.riskTone]}
                strokeWidth="1"
                strokeOpacity={0.4}
              />
              <text
                x={badgeX + badgeW / 2}
                y={rowCy + 3.5}
                textAnchor="middle"
                fontSize="9"
                fill={RISK_COLOR[fund.riskTone]}
                fontWeight="700"
              >
                {t.funds[i]!.riskLabel}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFundAssetMixDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <FundAssetMixIllustration />
    </IllustrationCard>
  );
}
